import { gql } from 'graphql-tag';
import dayjs from 'dayjs';
import { snappy } from 'src/_shared/services/utils.js';
import {
	QUERY_REWARDS_STORE_TRANSACTIONS_BY_USER_ID,
	CREATE_REWARDS_STORE_TRANSACTION,
	CREATE_REWARDS_STORE_TRANSACTION_SNAPPY,
	GET_REWARDS_STORE_SETTINGS,
	QUERY_REWARDS_STORE_REWARDS,
} from 'src/rewardsStore/RewardsStoreGraphQL';
import SnappyGiftService from 'src/my-rewards/service/SnappyGift.service.jsx';
import uuid from 'uuid/v4';
import { isBackfilledAssignment } from 'src/rewardsAndRecognitionHelper/utils.js';

export const AUTOMATED_TYPE_ID = '449e1150-ddac-485d-8321-537705c5bb40';

// Pin (milestone reward) states
export const PIN_STATE = {
	UNCLAIMED: 'unclaimed',
	STARTED: 'started',
	CLAIMED: 'claimed',
	DECLINED: 'declined',
	EXPIRED: 'expired',
};

// Gift (badge Snappy reward) states
export const GIFT_STATE = {
	UNCLAIMED: 'unclaimed',
	STARTED: 'started',
	CLAIMED: 'claimed',
	EXPIRED: 'expired',
	UNKNOWN: 'unknown',
};

const SNOOZE_MAX_HOURS = 24;

export const QUERY_MILESTONE_BADGE_ASSIGNMENTS = gql`
	query QueryMilestoneBadgeAssignments(
		$userId: ID!
		$limit: Int
		$nextToken: String
	) {
		queryBadgeAssignmentsByUserIdPostgres(
			userId: $userId
			limit: $limit
			nextToken: $nextToken
		) {
			items {
				id
				badgeId
				userId
				createdAt
				awardedBy
				certificateKey
				approvalStatus
				sentWithoutSnappyGrant
				badge {
					id
					name
					badgeTypeId
					milestoneYears
					milestoneRewardId
					milestoneReward {
						id
						name
						image
					}
					snappyGrantId
					snappyGrant {
						id
						name
						externalSnappyId
					}
				}
			}
			nextToken
		}
	}
`;

export const fetchMilestoneAssignments = async (client, userId) => {
	let allAssignments = [];
	let nextToken = null;
	do {
		const { data } = await client.query({
			query: QUERY_MILESTONE_BADGE_ASSIGNMENTS,
			variables: { userId, limit: 1000, nextToken },
			fetchPolicy: 'network-only',
		});
		const connection = data?.queryBadgeAssignmentsByUserIdPostgres;
		allAssignments = [...allAssignments, ...(connection?.items || [])];
		nextToken = connection?.items ? connection.nextToken : null;
	} while (nextToken);

	// Backfilled milestones never offer a pin or gift, so they are left out of
	// every claim flow (popups, claim tags, banners and the "to claim" count).
	return allAssignments.filter(
		(a) =>
			a?.badge?.badgeTypeId === AUTOMATED_TYPE_ID &&
			a.badge?.milestoneRewardId &&
			!isBackfilledAssignment(a)
	);
};

const fetchAllTransactions = async (client, userId, companyId) => {
	let allTransactions = [];
	let nextToken = null;
	do {
		const { data } = await client.query({
			query: QUERY_REWARDS_STORE_TRANSACTIONS_BY_USER_ID,
			variables: { userId, companyId, limit: 100, nextToken },
			fetchPolicy: 'network-only',
		});
		const connection = data?.queryRewardsStoreTransactionsByUserId;
		allTransactions = [...allTransactions, ...(connection?.items || [])];
		nextToken = connection?.items ? connection.nextToken : null;
	} while (nextToken);
	return allTransactions;
};

// The badge has both a milestone reward (e.g. a pin) and a Snappy reward (e.g. a gift)
export const hasPinAndGift = (assignment) =>
	Boolean(
		assignment?.badge?.milestoneRewardId &&
			assignment?.badge?.snappyGrant?.externalSnappyId &&
			!assignment?.sentWithoutSnappyGrant &&
			(!assignment?.approvalStatus || assignment.approvalStatus === 'Approved')
	);

export const getMilestoneYears = (badge) => {
	if (badge?.milestoneYears) return badge.milestoneYears;
	const match = badge?.name?.match(/(\d+)\s*-?\s*years?/i);
	return match ? Number(match[1]) : null;
};

const getPinState = (transaction, claimBy) => {
	if (!transaction) {
		if (claimBy && dayjs().isAfter(claimBy)) return PIN_STATE.EXPIRED;
		return PIN_STATE.UNCLAIMED;
	}

	if (transaction.status === 'declined') return PIN_STATE.DECLINED;
	if (transaction.status === 'completed') return PIN_STATE.CLAIMED;
	// A Snappy checkout link exists, but Enterprise can't confirm the order went through
	if (transaction.link) return PIN_STATE.STARTED;
	return PIN_STATE.CLAIMED;
};

const getGiftState = (snappyGift, statusData, claimBy) => {
	if (!snappyGift) {
		if (claimBy && dayjs().isAfter(claimBy)) return GIFT_STATE.EXPIRED;
		return GIFT_STATE.UNCLAIMED;
	}

	const isPastClaimBy = claimBy && dayjs().isAfter(claimBy);
	switch (statusData?.status) {
		case 'claimed':
			return GIFT_STATE.CLAIMED;
		case 'expired':
			return GIFT_STATE.EXPIRED;
		case 'opened':
		case 'unwrapped':
			return isPastClaimBy ? GIFT_STATE.EXPIRED : GIFT_STATE.STARTED;
		case 'unopened':
			return isPastClaimBy ? GIFT_STATE.EXPIRED : GIFT_STATE.UNCLAIMED;
		default:
			return GIFT_STATE.UNKNOWN;
	}
};

export const isPinOutstanding = (claim) =>
	claim?.pin?.state === PIN_STATE.UNCLAIMED;

export const isGiftOutstanding = (claim) =>
	claim?.gift?.state === GIFT_STATE.UNCLAIMED ||
	claim?.gift?.state === GIFT_STATE.STARTED;

export const needsAttention = (claim) =>
	isPinOutstanding(claim) || isGiftOutstanding(claim);

export const countOutstanding = (claim) =>
	(isPinOutstanding(claim) ? 1 : 0) + (isGiftOutstanding(claim) ? 1 : 0);

export const isPinResolved = (claim) =>
	[PIN_STATE.CLAIMED, PIN_STATE.STARTED, PIN_STATE.DECLINED].includes(
		claim?.pin?.state
	);

export const isGiftResolved = (claim) =>
	claim?.gift?.state === GIFT_STATE.CLAIMED;

/**
 * Builds the claim status for every milestone assignment that has a pin and a gift.
 * Pin status comes from rewards store transactions, gift status from SnappyGift + Snappy.
 */
export const loadCombinedMilestoneClaims = async ({
	client,
	currentUser,
	assignments,
}) => {
	const combined = (assignments || []).filter(hasPinAndGift);
	if (combined.length === 0) return [];

	const companyId = currentUser?.company?.id;
	const claimExpiryDays =
		currentUser?.company?.recognitionSettings?.milestoneClaimExpiryDays || 0;
	const snappyGiftService = new SnappyGiftService(client);

	const [transactions, snappyGifts, rewardsData] = await Promise.all([
		fetchAllTransactions(client, currentUser.id, companyId),
		snappyGiftService.querySnappyGiftsByUserIdCompanyId(
			currentUser.id,
			companyId
		),
		client.query({
			query: QUERY_REWARDS_STORE_REWARDS,
			variables: { companyId, rewardType: 'milestone', limit: 100 },
			fetchPolicy: 'cache-first',
		}),
	]);

	const milestoneRewards =
		rewardsData?.data?.queryRewardsStoreRewardsByCompanyIdRewardType?.items ||
		[];

	const claims = await Promise.all(
		combined.map(async (assignment) => {
			const rewardId = assignment.badge.milestoneRewardId;
			const reward = milestoneRewards.find(
				(r) => r.id === rewardId && r.active !== false
			);
			if (!reward) return null;

			const claimBy =
				claimExpiryDays > 0 && assignment.createdAt
					? dayjs(assignment.createdAt).add(claimExpiryDays, 'day')
					: null;

			const transaction = transactions
				.filter((t) => t.rewardId === rewardId)
				.sort((a, b) =>
					(b.dateCreated || '').localeCompare(a.dateCreated || '')
				)[0];

			const snappyGift = snappyGifts.find(
				(g) =>
					g.badgeAssignmentId === assignment.id &&
					g.externalSnappyGiftId &&
					g.externalSnappyGiftId !== 'undefined'
			);
			const statusData = snappyGift
				? await snappyGiftService.getSnappyGift(
						currentUser.id,
						snappyGift.externalSnappyGiftId
					)
				: null;
			const giftClaimBy = statusData?.expirationDate
				? dayjs(statusData.expirationDate)
				: claimBy;

			return {
				id: assignment.id,
				assignment,
				badgeName: assignment.badge.name,
				years: getMilestoneYears(assignment.badge),
				createdAt: assignment.createdAt,
				certificateKey: assignment.certificateKey,
				pin: {
					reward,
					image: reward.image || assignment.badge.milestoneReward?.image,
					transaction: transaction || null,
					link: transaction?.link || null,
					claimBy,
					state: getPinState(transaction, claimBy),
				},
				gift: {
					snappyGrant: assignment.badge.snappyGrant,
					snappyGift: snappyGift || null,
					link: snappyGift?.link || null,
					claimBy: giftClaimBy,
					status: statusData?.status || null,
					state: getGiftState(snappyGift, statusData, giftClaimBy),
				},
			};
		})
	);

	return claims
		.filter(Boolean)
		.sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
};

// Starts the pin claim in Snappy (same as the single milestone popup's Claim Gift)
export const createPinClaim = async ({ client, currentUser, reward }) => {
	const companyId = currentUser.company.id;

	if (reward.snappyProduct?.id) {
		const { data: settingsData } = await client.query({
			query: GET_REWARDS_STORE_SETTINGS,
			variables: { companyId },
			fetchPolicy: 'cache-first',
		});
		const { data } = await client.mutate({
			mutation: CREATE_REWARDS_STORE_TRANSACTION_SNAPPY,
			variables: {
				input: {
					companyId,
					userId: currentUser.id,
					campaignId:
						reward.snappyCampaignId ||
						settingsData?.getRewardsStoreSettings?.campaignId,
					productId: reward.snappyProduct.id,
					rewardId: reward.id,
					amount: 0,
					budget: reward.snappyProduct.budget,
					firstName: currentUser.firstName,
					lastName: currentUser.lastName,
					email: currentUser.emailAddress,
					phone: '',
					isMilestoneCampaign: Boolean(reward.snappyCampaignId),
				},
			},
		});
		return data?.createRewardsStoreTransactionSnappy || null;
	}

	const { data } = await client.mutate({
		mutation: CREATE_REWARDS_STORE_TRANSACTION,
		variables: {
			input: {
				companyId,
				userId: currentUser.id,
				rewardId: reward.id,
				amount: 0,
			},
		},
	});
	return data?.createRewardsStoreTransaction || null;
};

export const declinePinClaim = async ({ client, currentUser, reward }) => {
	await client.mutate({
		mutation: CREATE_REWARDS_STORE_TRANSACTION,
		variables: {
			input: {
				companyId: currentUser.company.id,
				userId: currentUser.id,
				rewardId: reward.id,
				amount: 0,
				isDecline: true,
			},
		},
	});
};

// Creates the Snappy gift link (same as the Claim Reward row for an uncreated gift)
export const createGiftClaim = async ({ currentUser, claim }) => {
	const { assignment, gift } = claim;
	const response = await snappy({
		type: 'directCreateGift',
		sendingMethod: 'link',
		snappyCampaignId: gift.snappyGrant.externalSnappyId,
		snappyGiftInput: {
			id: uuid(),
			companyId: currentUser.company.id,
			userId: currentUser.id,
			badgeAssignmentId: assignment.id,
			snappyGrantId: gift.snappyGrant.id,
			externalSnappyGiftId: gift.snappyGrant.externalSnappyId,
			claimedOn: new Date().toISOString(),
		},
	});
	return response?.link || null;
};

// "I'll claim later": hide until the next sign-in (sign-out clears localStorage) or 24 hours
const snoozeKey = (userId, claimId) => `milestoneClaimLater:${userId}:${claimId}`;

export const snoozeClaim = (userId, claimId) => {
	try {
		localStorage.setItem(snoozeKey(userId, claimId), String(Date.now()));
	} catch {}
};

export const isClaimSnoozed = (userId, claimId) => {
	try {
		const value = Number(localStorage.getItem(snoozeKey(userId, claimId)));
		return (
			value > 0 && Date.now() - value < SNOOZE_MAX_HOURS * 60 * 60 * 1000
		);
	} catch {
		return false;
	}
};

export const formatClaimBy = (date) =>
	date ? dayjs(date).format('MMM D, YYYY') : null;
